import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { Submittal, SubmittalAssignment, User, InvoiceItem } from '@/types/types';

interface UpdateAssignmentRequest {
    hours: number;
    remark: string;
}

interface CreateAssignmentRequest {
    userId: number;
    role: 'MAIN' | 'PEER' | 'FINAL';
}

interface CreateInvoiceItemRequest {
    hours: number;
    remark: string;
}

export const contractorDashboardApi = createApi({
    reducerPath: 'contractorDashboardApi',
    baseQuery: fetchBaseQuery({
        baseUrl: 'https://api.getspect.ai/v3',
        credentials: 'include'
    }),
    tagTypes: ['ContractorFiles', 'InvoiceItems'],
    endpoints: (builder) => ({
        getCompletedFiles: builder.query<Submittal[], void>({
            query: () => '/submittal/recently-completed',
            providesTags: ['ContractorFiles']
        }),
        getPendingFiles: builder.query<Submittal[], void>({
            query: () => '/submittal/released-to-contractor',
            providesTags: ['ContractorFiles']
        }),
        updateSubmittalAssignment: builder.mutation<SubmittalAssignment, { id: number; data: UpdateAssignmentRequest }>({
            query: ({ id, data }) => ({
                url: `/submittal-assignment/${id}`,
                method: 'PUT',
                body: data
            }),
            invalidatesTags: ['ContractorFiles']
        }),
        getContractors: builder.query<User[], void>({
            query: () => '/user/contractors'
        }),
        deleteSubmittalAssignment: builder.mutation<void, number>({
            query: (id) => ({
                url: `/submittal-assignment/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['ContractorFiles']
        }),
        createSubmittalAssignment: builder.mutation<void, { fileId: number; assignments: CreateAssignmentRequest[] }>({
            query: ({ fileId, assignments }) => ({
                url: `/submittal/${fileId}/assign`,
                method: 'PUT',
                body: assignments
            }),
            invalidatesTags: ['ContractorFiles']
        }),
        updateSubmittalAssignmentRole: builder.mutation<void, { id: number; role: 'MAIN' | 'PEER' | 'FINAL' }>({
            query: ({ id, role }) => ({
                url: `/submittal-assignment/${id}`,
                method: 'PUT',
                body: { role }
            }),
            invalidatesTags: ['ContractorFiles']
        }),
        getInvoiceItems: builder.query<InvoiceItem[], void>({
            query: () => '/invoice-item',
            providesTags: ['InvoiceItems']
        }),
        createInvoiceItem: builder.mutation<InvoiceItem, CreateInvoiceItemRequest>({
            query: (data) => ({
                url: '/invoice-item',
                method: 'POST',
                body: data
            }),
            invalidatesTags: ['InvoiceItems']
        }),
        deleteInvoiceItem: builder.mutation<void, number>({
            query: (id) => ({
                url: `/invoice-item/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['InvoiceItems']
        })
    })
});

export const {
    useGetCompletedFilesQuery,
    useGetPendingFilesQuery,
    useLazyGetCompletedFilesQuery,
    useLazyGetPendingFilesQuery,
    useUpdateSubmittalAssignmentMutation,
    useGetContractorsQuery,
    useDeleteSubmittalAssignmentMutation,
    useCreateSubmittalAssignmentMutation,
    useUpdateSubmittalAssignmentRoleMutation,
    useGetInvoiceItemsQuery,
    useCreateInvoiceItemMutation,
    useDeleteInvoiceItemMutation
} = contractorDashboardApi;
